import { google } from 'calendar-link';
import _compact from 'lodash/compact';
import _isEmpty from 'lodash/isEmpty';
import { RRule } from 'rrule';

import {
  CronFrequencyEnum,
  CustomFrequencyEnum,
  DayOfWeekEnum,
  WeekOfMonthEnum,
} from '@/types/schema';

import { CalendarEventProps } from './types';

const FREQ_MAP = {
  [CronFrequencyEnum.Daily]: RRule.DAILY,
  [CronFrequencyEnum.Monthly]: RRule.MONTHLY,
  [CronFrequencyEnum.Weekly]: RRule.WEEKLY,
  [CronFrequencyEnum.Fortnightly]: RRule.WEEKLY,
  [CronFrequencyEnum.Quarterly]: RRule.MONTHLY,
  [CronFrequencyEnum.Yearly]: RRule.YEARLY,
  [CronFrequencyEnum.Hourly]: RRule.HOURLY,
};

const INTERVAL_MAP = {
  [CronFrequencyEnum.Daily]: 1,
  [CronFrequencyEnum.Monthly]: 1,
  [CronFrequencyEnum.Weekly]: 1,
  [CronFrequencyEnum.Fortnightly]: 2,
  [CronFrequencyEnum.Quarterly]: 3,
  [CronFrequencyEnum.Yearly]: 1,
  [CronFrequencyEnum.Hourly]: 1,
};

const getDayMap = (freq: number) => ({
  [DayOfWeekEnum.Monday]: RRule.MO.nth(freq),
  [DayOfWeekEnum.Tuesday]: RRule.TU.nth(freq),
  [DayOfWeekEnum.Wednesday]: RRule.WE.nth(freq),
  [DayOfWeekEnum.Thursday]: RRule.TH.nth(freq),
  [DayOfWeekEnum.Friday]: RRule.FR.nth(freq),
  [DayOfWeekEnum.Saturday]: RRule.SA.nth(freq),
  [DayOfWeekEnum.Sunday]: RRule.SU.nth(freq),
});

const COUNT_MAP = {
  [CronFrequencyEnum.Daily]: 365,
  [CronFrequencyEnum.Monthly]: 12,
  [CronFrequencyEnum.Weekly]: 52,
  [CronFrequencyEnum.Fortnightly]: 26,
  [CronFrequencyEnum.Quarterly]: 4,
  [CronFrequencyEnum.Yearly]: 1,
  [CronFrequencyEnum.Hourly]: 1,
};

const CUSTOM_FREQ_MAP = {
  [CustomFrequencyEnum.Minute]: RRule.MINUTELY,
  [CustomFrequencyEnum.Day]: RRule.DAILY,
  [CustomFrequencyEnum.Week]: RRule.WEEKLY,
  [CustomFrequencyEnum.Month]: RRule.MONTHLY,
  [CustomFrequencyEnum.Year]: RRule.YEARLY,
};

const CUSTOM_COUNT_MAP = {
  [CustomFrequencyEnum.Minute]: 525600,
  [CustomFrequencyEnum.Day]: 365,
  [CustomFrequencyEnum.Week]: 52,
  [CustomFrequencyEnum.Month]: 12,
  [CustomFrequencyEnum.Year]: 1,
};

const getFreqPos = (weekOfMonth?: WeekOfMonthEnum | null) => {
  switch (weekOfMonth) {
    case WeekOfMonthEnum.First:
      return 1;
    case WeekOfMonthEnum.Second:
      return 2;
    case WeekOfMonthEnum.Third:
      return 3;
    case WeekOfMonthEnum.Fourth:
      return 4;
    case WeekOfMonthEnum.Last:
      return -1;
    default:
      return 1;
  }
};

export function getGoogleCalendarLink(args: CalendarEventProps) {
  const calendar_link = google({
    start: new Date(args.start),
    end: args.end ? new Date(args.end) : undefined,
    organizer: { name: args.organiser.name, email: args.organiser.emailId },
    title: args.title,
    description: args.description,
    guests: args.guests ? _compact(args.guests.map((it) => it.emailId)) : undefined,
    duration: [args.duration || 30, 'minutes'],
  });

  const schedule = args.schedule;
  if (!schedule) {
    return calendar_link;
  }

  if (schedule.frequency === CronFrequencyEnum.Custom) {
    const custom = schedule.custom!;
    const recurrenceRule = new RRule({
      freq: CUSTOM_FREQ_MAP[custom.frequency],
      interval: custom.interval,
      count: args.until ? undefined : CUSTOM_COUNT_MAP[custom.frequency] * 2,
      until: args.until ?? undefined,
      byweekday: _isEmpty(custom.daysOfWeek)
        ? null
        : custom.daysOfWeek?.map((day) => getDayMap(getFreqPos(custom.weekOfMonth))[day]),
    }).toString();

    return `${calendar_link}&recur=${encodeURIComponent(recurrenceRule)};`;
  }

  const recurrenceRule = new RRule({
    freq: FREQ_MAP[schedule.frequency],
    interval: INTERVAL_MAP[schedule.frequency],
    count: args.until ? undefined : COUNT_MAP[schedule.frequency],
    until: args.until ?? undefined,
    byweekday: _isEmpty(schedule.daysOfWeek)
      ? null
      : schedule.daysOfWeek?.map((day) => getDayMap(getFreqPos(schedule.weekOfMonth))[day]),
  }).toString();

  return `${calendar_link}&recur=${encodeURIComponent(recurrenceRule)};`;
}
